// Base of appController: forwards UI events to the experience store actions, and sets
// application values through the plugin hooks.
import { setApplicationValue } from "@/stores/experience/actions/layout";
import { exitActionSheet, linkCta } from "@/stores/experience/actions/links";
import {
  setVideoPause,
  setVideoPosition,
  timeSync,
  videoEnded,
  videoMarkerReached,
  videoMarkerSelected,
} from "@/stores/experience/actions/timeline";
import {
  addArticlesToFavorites,
  addFavorite,
  dismissFavorites,
  removeArticlesFromFavorites,
  showFavorites,
} from "@/stores/experience/actions/favorites";
import {
  articleTransitionEnd,
  articleTransitionStart,
  closeShopWindow,
  decrementShopWindowIndex,
  incrementShopWindowIndex,
  openShopWindow,
  setShopWindowIndex,
  userInteractionEnd,
  userInteractionStart,
} from "@/stores/experience/actions/shopWindow";
import { setExperienceData } from "@/stores/experience/actions/loading";
import type { ExperienceState } from "@/types/state";
import type ExperienceData from "@/modules/ExperienceData";

export default class AppActions {
  declare state: ExperienceState;

  setExperienceData(experienceData: ExperienceData) {
    setExperienceData(experienceData);
  }

  decrementShopWindowIndex(region: string, name: string) {
    decrementShopWindowIndex(region, name);
  }

  incrementShopWindowIndex(region: string, name: string) {
    incrementShopWindowIndex(region, name);
  }

  setShopWindowIndex(region: string, name: string, index: number) {
    setShopWindowIndex(region, name, index);
  }

  onUserInteractionStart(region: string) {
    userInteractionStart(region);
  }

  onUserInteractionEnd(region: string) {
    userInteractionEnd(region);
  }

  onArticleTransitionStart(region: string, name: string, index: number) {
    articleTransitionStart(region, name, index);
  }

  onArticleTransitionEnd(region: string, name: string, index: number) {
    articleTransitionEnd(region, name, index);
  }

  addFavorite(region: string, name: string, cindex: number, index: number) {
    addFavorite(region, name, cindex, index);
  }

  linkCta(data: unknown) {
    linkCta(data);
  }

  updateFavorites(region: string, items: { id: unknown; checked: boolean }[]) {
    const add: unknown[] = [];
    const remove: unknown[] = [];
    for (let n = 0, max = items.length; n < max; n++) {
      if (items[n].checked) {
        add.push(items[n].id);
      } else {
        remove.push(items[n].id);
      }
    }

    if (add.length > 0) {
      addArticlesToFavorites(region, add);
    }

    if (remove.length > 0) {
      removeArticlesFromFavorites(region, remove);
    }
  }

  showFavorites(region: string) {
    showFavorites(region);
  }

  dismissFavorites(region: string) {
    dismissFavorites(region);
  }

  setApplicationValue(region: string, key: string, value: unknown) {
    const applicationData = this.state.applicationData;
    // biome-ignore lint/suspicious/noDoubleEquals: an unset key (undefined) and a null value count as equal, so a null write to an unset key does not run the set hooks
    if (applicationData.getValue(key) != value) {
      this.state.plugin.willSetValue(this.state, key, value);
      setApplicationValue(region, key, value);
      this.state.plugin.didSetValue(this.state, key, value);
    }
  }

  onApplicationEvent(event: unknown) {
    this.state.plugin.handleEvent(this.state, event);
  }

  openShopWindow(region: string) {
    openShopWindow(region);
  }

  closeShopWindow(region: string) {
    closeShopWindow(region);
  }

  play(region: string) {
    setVideoPause(region, false);
  }

  onSetVideoPosition(region: string, position: number) {
    setVideoPosition(region, position);
  }

  onVideoMarkerSelected(region: string, timelineName: string, marker: unknown) {
    videoMarkerSelected(region, timelineName, marker);
  }

  onVideoMarkerReached(region: string, timelineName: string, marker: unknown) {
    videoMarkerReached(region, timelineName, marker);
  }

  onVideoEnded(timelineName: string) {
    videoEnded(timelineName);
  }

  onActionSheetExit(
    region: string,
    name: string,
    cindex: number,
    index: number,
  ) {
    exitActionSheet(region, name, cindex, index);
  }

  onTimeSync(region: string, name: string, time: number, duration: number) {
    timeSync(name, time, duration);
  }
}
